import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Input from '../../components/input/Input';
import DropDown from '../../components/dropdown/DropDown';
import DatePicker from '../../components/datepicker/DatePicker';
import { FormValues } from '../../pages/MonetizationNew';
import { useEffect } from 'react';

interface CheckPromissoryExecutionSectionProps {
  formValues: FormValues;
  updateFormValues: (updates: Partial<FormValues>) => void;
  clearing?: number;
  isCheck: boolean;
  isCommercialEndorsement?: boolean;
}

const CheckPromissoryExecutionSection: React.FC<CheckPromissoryExecutionSectionProps> = ({
  formValues,
  updateFormValues,
  clearing,
  isCheck,
  isCommercialEndorsement = false,
}) => {
  const ivaOptions = [
    { id: true, label: 'SI' },
    { id: false, label: 'NO' },
  ];

  const clearingOptions = [
    { id: 0, label: 'NO' },
    { id: 1, label: '24 hs' },
    { id: 2, label: '48 hs' },
  ];

  // Note: liquidation date calculation is now handled in useMonetizationForm hook

  // Set clearing from API if it's a check
  useEffect(() => {
    if (isCheck && clearing !== undefined && clearing !== null) {
      updateFormValues({ clearing });
    }
  }, [clearing, isCheck]);

  const formatNumber = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined || isNaN(amount) || amount === 0) return '';
    if (Number.isInteger(amount)) {
      return amount.toLocaleString('es-AR');
    }
    return amount.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const parseNumber = (value: string): number | null => {
    const cleanedValue = value.replace(/\./g, '').replace(',', '.');
    const parsed = parseFloat(cleanedValue);
    return isNaN(parsed) ? null : parsed;
  };

  return (
    <Card margin={{ top: '4' }}>
      <Card margin={{ bottom: '3' }}>
        <H variant="secondary-2" type='4' weight='bold' modifier='strong'>
          Ejecución de aval
        </H>
      </Card>

      {/* Fila 1 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px', alignItems: 'end' }}>
        <Card>
          <P withMargin={false} variant='neutral' type='4' modifier='strong'>
            Fecha de monetización *
          </P>
          <Card margin={{ top: '2' }}>
            <DatePicker
              defaultValue={formValues.monetization_date}
              onChange={(date: string) => updateFormValues({ monetization_date: date })}
              disabled={false}
              displayFormat="DD/MM/AA"
            />
          </Card>
        </Card>
        {!isCommercialEndorsement && (
          <Input
            label="Código CVBA *"
            id='execution-cvba-code'
            type='text'
            size='md'
            value={formValues.monetization_cvba_code || ''}
            onChange={(e) => updateFormValues({ monetization_cvba_code: e.target.value })}
            inputStatus="normal"
          />
        )}
        <Input
          label="Monto Total en Pesos *"
          id='execution-total-amount'
          type='text'
          size='md'
          value={formatNumber(formValues.monetization_amount)}
          onChange={(e) => updateFormValues({ monetization_amount: parseNumber(e.target.value) })}
          inputStatus="normal"
        />
        <DropDown
          id='execution-iva-dropdown'
          label="IVA *"
          items={ivaOptions}
          size='md'
          title={formValues.iva !== undefined ? (formValues.iva ? 'SI' : 'NO') : 'Seleccionar'}
          onChange={(value) => {
            const selected = ivaOptions.find(opt => opt.label === value);
            updateFormValues({ iva: selected?.id });
          }}
          marginTop="4px"
        />
        {isCommercialEndorsement && (
          <Input
            label="Tasa de Descuento *"
            id='execution-discount-rate'
            type='text'
            size='md'
            value={formatNumber(formValues.monetization_discount_rate || null)}
            onChange={(e) => updateFormValues({ monetization_discount_rate: parseNumber(e.target.value) })}
            inputStatus="normal"
          />
        )}
      </div>

      {/* Fila 2 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px', alignItems: 'end' }}>
        {!isCommercialEndorsement && (
          <Input
            label="Tasa de Descuento *"
            id='execution-discount-rate'
            type='text'
            size='md'
            value={formatNumber(formValues.monetization_discount_rate || null)}
            onChange={(e) => updateFormValues({ monetization_discount_rate: parseNumber(e.target.value) })}
            inputStatus="normal"
          />
        )}
        <Input
          label="Intereses *"
          id='execution-interest'
          type='text'
          size='md'
          value={formatNumber(formValues.monetization_interest || null)}
          onChange={(e) => updateFormValues({ monetization_interest: parseNumber(e.target.value) })}
          inputStatus="normal"
        />
        <Input
          label="Fecha de liquidación"
          id='execution-liquidation-date'
          type='text'
          size='md'
          value={formValues.settlement_date || ''}
          readOnly
          modifier="weak"
        />
        {isCheck ? (
          <Input
            label="Plazo de liquidación *"
            id='execution-clearing-input'
            type='text'
            size='md'
            value={
              formValues.clearing !== null && formValues.clearing !== undefined
                ? clearingOptions.find(opt => opt.id === formValues.clearing)?.label || ''
                : ''
            }
            readOnly
            modifier="weak"
          />
        ) : (
          <DropDown
            id='execution-clearing-dropdown'
            label="Plazo de liquidación *"
            items={clearingOptions}
            size='md'
            title={
              formValues.clearing !== null && formValues.clearing !== undefined
                ? clearingOptions.find(opt => opt.id === formValues.clearing)?.label || 'Seleccionar'
                : 'Seleccionar'
            }
            onChange={(value) => {
              const selected = clearingOptions.find(opt => opt.label === value);
              updateFormValues({ clearing: selected?.id });
            }}
            marginTop="4px"
          />
        )}
        {isCommercialEndorsement && (
          <Input
            label="Alyc Compradora *"
            id='execution-buyer-alyc'
            type='text'
            size='md'
            value={formValues.buyer_alyc || ''}
            onChange={(e) => updateFormValues({ buyer_alyc: e.target.value })}
            inputStatus="normal"
          />
        )}
      </div>

      {/* Fila 3 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', alignItems: 'end' }}>
        {!isCommercialEndorsement && (
          <Input
            label="Alyc Compradora *"
            id='execution-buyer-alyc'
            type='text'
            size='md'
            value={formValues.buyer_alyc || ''}
            onChange={(e) => updateFormValues({ buyer_alyc: e.target.value })}
            inputStatus="normal"
          />
        )}
        <Input
          label="Alyc Vendedora *"
          id='execution-seller-alyc'
          type='text'
          size='md'
          value={formValues.seller_alyc || ''}
          onChange={(e) => updateFormValues({ seller_alyc: e.target.value })}
          inputStatus="normal"
        />
      </div>
    </Card>
  );
};

export default CheckPromissoryExecutionSection;
